/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보 결과 로직 관련 API
 * 버전 : 1.0
 * 파일명 : Promo.ts
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-15, 강동형 : 저작권 추가, 삭제 토스트 팝업 추가
 * 수정 : 2025-07-16, 강동형 : 페이지 당 갯수 10 -> 20개로 변경
 * 수정 : 2025-07-17, 강동형 : 토스트 팝업 로직 위치 조정
 * 수정 : 2025-07-18, 강동형 : 관심표명자 등록 실패 토스트 팝업 추가
 * 수정 : 2026-02-05, 강동형 : 관심표명자 학과 등록 기능 추가
 * ================================================================ */

import axios from 'axios';
import { getAuthToken } from './Auth';
import { postPromotionType } from 'src/types/PromoType';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL

export interface PromoResultFilterType {
    page: number;
    start_date?:string | Date;
    end_date?:string | Date;
    loc:[string,string[]][];
    edu_level:number[];
    highschool_type:number[];
    special_note:number[];
    sort_by: string;
    sort_order: string;
    school_name: string
}

export const postPromoFilter = async (params:PromoResultFilterType)=>{
    try{
        const token = getAuthToken()
        const payload: any = {
          loc: params.loc,
          edu_level: params.edu_level,
          highschool_type: params.highschool_type,
          special_note: params.special_note,
          school_name : params.school_name
          };
          // 날짜 필드가 존재할 때만 추가
          if (params.start_date) payload.start_date = format(params.start_date, 'yyyy-MM-dd');
          if (params.end_date) payload.end_date = format(params.end_date, 'yyyy-MM-dd');

        
        const response = await axios.post(`${API_URL}/search-promotion-results-v2`,payload,
          {
            params :{
              page:params.page,
              page_size:20,
              sort_by: params.sort_by,
              sort_order: params.sort_order
            },
            headers:{
              "Content-Type":"application/json",
              Authorization: `Bearer ${token}`
            }
          },)     
        return response.data
    }
    catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
              const response = error.response
              return response.data
            }
          }
    }
}

export const postPromotion = async({visit_date,school_id, school_class, total_cnt, attendee_cnt, interested_cnt, apply_cnt, content}:postPromotionType)=>{
  try{
    const token = getAuthToken()
    if(school_class === 'high'){
      const response = await axios.post(`${API_URL}/promotion/highschool`,{
        visit_date,
        school_id,
        total_cnt,
        attendee_cnt,
        content
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    }
    else if (school_class === 'univ'){
      const response = await axios.post(`${API_URL}/promotion/university`,{
        visit_date,
        education_id:school_id,
        total_cnt,
        attendee_cnt,
        content
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    } else{
      const response = await axios.post(`${API_URL}/promotion/training_center`,{
        center_id: school_id,
        visit_date,
        total_cnt,
        attendee_cnt,
        content
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    }
    }
    catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
              const response = error.response
              if(response.status === 409){
                toast.error('이미 작성된 홍보 결과가 있습니다.',{autoClose:2000})
              }
              else{
                toast.error('홍보 결과 등록에 실패했습니다.',{autoClose:2000})
              }
              return {
                status: response.status,
                data: response.data
              }
            }
          }
    }
}

interface InterestStudentState{
  promotion_id : number,
  name: string,
  birthday:Date|string,
  gender: string,
  input_date:Date|string,
  phone:string,
  status:string,
  memo:string
  school_class: string
  major_name: string
}

export const postInterestStudent = async({promotion_id,name,birthday,gender,input_date,phone,status,memo, school_class, major_name}:InterestStudentState)=>{
  try{
    const token = getAuthToken()
    if(school_class === 'high'){
      const response = await axios.post(`${API_URL}/highschool-interested-student`,{
        name,
        promotion_id,
        birthday,
        gender,
        input_date,
        phone,
        status,
        memo,
        major_name
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    }
    else if (school_class === 'univ'){
      const response = await axios.post(`${API_URL}/university-interested-student`,{
        name,
        promotion_id,
        birthday,
        gender,
        input_date,
        phone,
        status,
        memo,
        major_name
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    }
    else{
      const response = await axios.post(`${API_URL}/tc-interested-student`,{
        name,
        promotion_id,
        birthday,
        gender,
        input_date,
        phone,
        status,
        memo,
        major_name
    },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      },)
      return response.data
    } 
    }
    catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
              const response = error.response
                toast.error('관심 표명자 등록에 실패했습니다.',{autoClose:2000})

              return response.data
            }
          }
    }
}

interface patchPromoState{
  result_id:number,
  total_cnt: number,
  attendee_cnt: number,
  apply_cnt: number,
  visit_date: Date|string,
  content: string,
  is_reviewed: boolean
  school_class: string
}

export const patchPromotion = async({result_id,total_cnt,attendee_cnt,apply_cnt,visit_date,content,is_reviewed,school_class}:patchPromoState)=>{
  try{
    const token = getAuthToken()
    if(school_class === 'high'){
    const response = await axios.patch(`${API_URL}/promotion/highschool/${result_id}`,{
      total_cnt,
      attendee_cnt,
      // apply_cnt,
      visit_date,
      content,
      is_reviewed
    },{
      headers:{
        "Content-Type":"application/json",
        Authorization: `Bearer ${token}`
      }
    })
      return response.data
    }
    else if (school_class === 'univ'){ 
      const response = await axios.patch(`${API_URL}/promotion/university/${result_id}`,{
        total_cnt,
        attendee_cnt,
        visit_date,
        content,
        is_reviewed
      },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
      }
    else{
      const response = await axios.patch(`${API_URL}/promotion/tc/${result_id}`,{
        total_cnt,
        attendee_cnt,
        visit_date,
        content,
        is_reviewed
      },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
    }
    }
  catch(error){
    if(axios.isAxiosError(error)){
        if(error.response){
          const response = error.response
          return response.data
        }
      }
  }
  }

  export const deletePromotion = async(result_id:number, school_class:string)=>{
    try{
      const token = getAuthToken()
      if(school_class === 'high'){
      const response = await axios.delete(`${API_URL}/highschool-promo-result/${result_id}`,{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
      }
      else if (school_class === 'univ'){
        const response = await axios.delete(`${API_URL}/university-promo-result/${result_id}`,{
          headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
        })
          return response.data
        }
      else {
        const response = await axios.delete(`${API_URL}/tc-promo-result/${result_id}`,{
          headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
        })
          return response.data
      }
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            if(response.status === 403){
              toast.error('권한이 없습니다.',{autoClose:2000})
            }
            return response.data
          }
        }
    }
    }